import { useMemo } from 'react';

import type { OrgChartRow } from '../support/orgScope';
import {
  deliveriesByOwner,
  filterDeliveriesByWindow,
  subtreeMetrics,
  subtreePipeline,
  withScheduledStatus,
  type DateWindow,
  type DeliveryMetrics,
  type PipelineMetrics,
} from '../support/campaignLeader';
import {
  cohortsByEmail as indexCohorts,
  enrollmentsByUser,
  subtreeEnrollmentMetrics,
  subtreeEnrollmentMetricsFromCohorts,
  type EnrollmentMetrics,
} from '../support/campaignEnrollmentLeader';
import {
  cohortsFromEnrollmentRollup,
  deliveriesRollupUsable,
  deliveryMetricsFromRollup,
  enrollmentMetricsFromRollup,
  pipelineMetricsFromRollup,
  rollupMembersByEmail,
  subtreeFiguresFromMembers,
} from '../support/campaignRollup';
import type { CampaignEnrollment } from '../support/campaigns';
import type { CompletionCohort } from '../support/campaignSalesLevels';
import {
  useCampaignCompletion,
  useFetchAllDeliveries,
  useFetchAllEnrollments,
  useFetchAllWorkshopMeetings,
} from './campaign-hooks';
import { useCampaignPipeline } from './useCampaignPipeline';
import { useCampaignDeliveriesRollup, useCampaignEnrollmentRollup } from './useCampaignRollups';

/** `fetchItems` returns one page of this many rows; a browser-side result that hits it may be incomplete. */
export const CAMPAIGN_PAGE_CAP = 1000;

interface Args {
  /** The leader whose subtree to summarise (for an individual, their own email). */
  manager: string;
  orgRows: OrgChartRow[];
  /** One campaign, or undefined for all campaigns. */
  campaignId?: number;
  /** Inclusive fiscal window applied to deliveries; null/undefined = all time. Enrollments are never windowed. */
  window?: DateWindow | null;
  /** Hold every request (e.g. the fiscal calendar is still loading). */
  enabled?: boolean;
}

export interface CampaignRollupData {
  /** Null until loaded. */
  deliveries: DeliveryMetrics | null;
  pipeline: PipelineMetrics | null;
  enrollment: EnrollmentMetrics | null;
  /** A person's team figures (their subtree), for a team-table row. */
  deliveryFor: (email: string) => { metrics: DeliveryMetrics; pipelineTotal: number };
  /** Per-person enablement, for the team table's Enablement column and reminders. */
  cohortsByEmail: Map<string, CompletionCohort>;
  byUser: Map<string, CampaignEnrollment[]>;
  deliveriesLoading: boolean;
  enrollmentLoading: boolean;
  /** A load error from the browser-side fallback (server failures fall back silently). */
  error: Error | null;
  /** Where each half came from. */
  source: { deliveries: 'server' | 'client'; enrollment: 'server' | 'client' };
  /** Only the browser-side fallback can be page-capped; true when it hit the cap. */
  mayBeIncomplete: boolean;
}

/**
 * The Campaigns tab's numbers for one leader's subtree, all from one place so the dashboard and the team
 * table agree. Prefers the server rollups (`deliveries-rollup`, `enrollment-rollup`: no row cap, no
 * browser-side org walk). If a rollup fails, or answers from an older server that ignores the date window or
 * lacks per-member status counts, it falls back to computing from the full tables in the browser, so a
 * deploy-order mismatch can never show figures for the wrong period.
 */
export function useCampaignRollupData({
  manager,
  orgRows,
  campaignId,
  window = null,
  enabled = true,
}: Args): CampaignRollupData {
  const man = String(manager || '').trim().toLowerCase();

  // ---- deliveries: server rollup, else browser-side ----
  const rollupQ = useCampaignDeliveriesRollup({ manager: man, campaignId, window, enabled });
  const serverDeliveries = rollupQ.isSuccess && deliveriesRollupUsable(rollupQ.data, window);
  const deliveriesPending = enabled && !!man && rollupQ.isLoading;
  const clientDeliveries = enabled && !deliveriesPending && !serverDeliveries;

  const rawDeliveriesQ = useFetchAllDeliveries({ enabled: clientDeliveries });
  // "Scheduled" is derived from booked workshop meetings (nothing stores it on the delivery); the server
  // rollup does the same, so the browser path needs the meetings too.
  const meetingsQ = useFetchAllWorkshopMeetings({ enabled: clientDeliveries });
  const { targetsByDelivery, amountByOpp } = useCampaignPipeline({ enabled: clientDeliveries });

  const rawDeliveries = rawDeliveriesQ.data;
  const meetings = meetingsQ.data;
  const byOwner = useMemo(() => {
    const forCampaign =
      campaignId == null ? rawDeliveries ?? [] : (rawDeliveries ?? []).filter((d) => d.campaign === campaignId);
    return deliveriesByOwner(filterDeliveriesByWindow(withScheduledStatus(forCampaign, meetings), window));
  }, [rawDeliveries, meetings, campaignId, window]);

  const members = useMemo(
    () => (serverDeliveries && rollupQ.data ? rollupMembersByEmail(rollupQ.data) : null),
    [serverDeliveries, rollupQ.data],
  );

  const deliveryFor = useMemo(
    () => (email: string) => {
      if (members) return subtreeFiguresFromMembers(email, orgRows, members);
      return {
        metrics: subtreeMetrics(email, orgRows, byOwner),
        pipelineTotal: subtreePipeline(email, orgRows, byOwner, targetsByDelivery, amountByOpp).total,
      };
    },
    [members, orgRows, byOwner, targetsByDelivery, amountByOpp],
  );

  const deliveries = useMemo<DeliveryMetrics | null>(() => {
    if (serverDeliveries && rollupQ.data) return deliveryMetricsFromRollup(rollupQ.data);
    if (clientDeliveries && rawDeliveries && !meetingsQ.isLoading) return subtreeMetrics(man, orgRows, byOwner);
    return null;
  }, [serverDeliveries, rollupQ.data, clientDeliveries, rawDeliveries, meetingsQ.isLoading, man, orgRows, byOwner]);

  const pipeline = useMemo<PipelineMetrics | null>(() => {
    if (serverDeliveries && rollupQ.data) return pipelineMetricsFromRollup(rollupQ.data);
    if (clientDeliveries && rawDeliveries && !meetingsQ.isLoading) {
      return subtreePipeline(man, orgRows, byOwner, targetsByDelivery, amountByOpp);
    }
    return null;
  }, [serverDeliveries, rollupQ.data, clientDeliveries, rawDeliveries, meetingsQ.isLoading, man, orgRows, byOwner, targetsByDelivery, amountByOpp]);

  // ---- enrollment: server rollup, else per-campaign cohorts, else the raw table ----
  const enrollQ = useCampaignEnrollmentRollup({ manager: man, campaignId, enabled });
  const serverEnrollment = enrollQ.isSuccess;
  const enrollmentPending = enabled && !!man && enrollQ.isLoading;
  const clientEnrollment = enabled && !enrollmentPending && !serverEnrollment;

  const completion = useCampaignCompletion(campaignId, { by: 'owner', manager: man }, { enabled: clientEnrollment });
  const useCohorts = campaignId != null && !completion.isError;
  const rawEnrollmentsQ = useFetchAllEnrollments({ enabled: clientEnrollment && !useCohorts });
  const rawEnrollments = rawEnrollmentsQ.data;

  const cohortIndex = useMemo(() => {
    if (serverEnrollment && enrollQ.data) return indexCohorts(cohortsFromEnrollmentRollup(enrollQ.data));
    if (clientEnrollment && useCohorts) return indexCohorts(completion.data?.cohorts ?? []);
    return new Map<string, CompletionCohort>();
  }, [serverEnrollment, enrollQ.data, clientEnrollment, useCohorts, completion.data]);

  const byUser = useMemo(() => {
    if (!clientEnrollment || useCohorts) return new Map<string, CampaignEnrollment[]>();
    const forCampaign =
      campaignId == null ? rawEnrollments ?? [] : (rawEnrollments ?? []).filter((e) => e.campaign === campaignId);
    return enrollmentsByUser(forCampaign, orgRows);
  }, [clientEnrollment, useCohorts, rawEnrollments, campaignId, orgRows]);

  const enrollmentLoading =
    enrollmentPending || (clientEnrollment && (useCohorts ? completion.isLoading : rawEnrollmentsQ.isLoading));

  const enrollment = useMemo<EnrollmentMetrics | null>(() => {
    if (enrollmentLoading) return null;
    if (serverEnrollment && enrollQ.data) return enrollmentMetricsFromRollup(enrollQ.data);
    if (!clientEnrollment) return null;
    return useCohorts
      ? subtreeEnrollmentMetricsFromCohorts(man, orgRows, cohortIndex)
      : subtreeEnrollmentMetrics(man, orgRows, byUser);
  }, [enrollmentLoading, serverEnrollment, enrollQ.data, clientEnrollment, useCohorts, man, orgRows, cohortIndex, byUser]);

  const mayBeIncomplete =
    (clientDeliveries && (rawDeliveries?.length ?? 0) >= CAMPAIGN_PAGE_CAP) ||
    (clientEnrollment && !useCohorts && (rawEnrollments?.length ?? 0) >= CAMPAIGN_PAGE_CAP);

  return {
    deliveries,
    pipeline,
    enrollment,
    deliveryFor,
    cohortsByEmail: cohortIndex,
    byUser,
    deliveriesLoading: deliveriesPending || (clientDeliveries && (rawDeliveriesQ.isLoading || meetingsQ.isLoading)),
    enrollmentLoading,
    error: clientDeliveries ? ((rawDeliveriesQ.error as Error | null) ?? null) : null,
    source: {
      deliveries: serverDeliveries ? 'server' : 'client',
      enrollment: serverEnrollment ? 'server' : 'client',
    },
    mayBeIncomplete,
  };
}
